'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { PDFDocument } from 'pdf-lib';
import { getToolAuthToken } from '@/lib/browser-tool-usage';
import {
  decodeToPngFile,
  SOFTWARE_DECODED,
  SOFTWARE_DECODED_ACCEPT,
  UNDECODABLE_MESSAGE,
} from '@/lib/software-image-decode';

type PageSizePreset = 'auto' | 'a4' | 'letter';
type PageOrientation = 'auto' | 'portrait' | 'landscape';

type PdfImagePage = {
  id: string;
  file: File;
  name: string;
  size: number;
  width: number;
  height: number;
  previewUrl: string;
  rotation: 0 | 90 | 180 | 270;
};

const pageSizeOptions: Array<{
  id: PageSizePreset;
  label: string;
  hint: string;
}> = [
  {
    id: 'auto',
    label: 'Auto fit',
    hint: 'Each page matches the image size plus margin.',
  },
  {
    id: 'a4',
    label: 'A4',
    hint: 'Good for standard document sharing and printing.',
  },
  {
    id: 'letter',
    label: 'Letter',
    hint: 'A common fit for U.S. document workflows.',
  },
];

const orientationOptions: Array<{
  value: PageOrientation;
  label: string;
}> = [
  { value: 'auto', label: 'Auto' },
  { value: 'portrait', label: 'Portrait' },
  { value: 'landscape', label: 'Landscape' },
];

const marginOptions = [
  { value: 18, label: 'Tight' },
  { value: 30, label: 'Balanced' },
  { value: 42, label: 'Airy' },
] as const;

const maxRenderEdge = 2400;

function formatFileSize(size: number) {
  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

function formatExportTimestamp(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}${month}${day}-${hours}${minutes}`;
}

function createExportFileName(pageCount: number) {
  const pageLabel = pageCount === 1 ? '1-page' : `${pageCount}-pages`;
  return `cleanor.app-image-to-pdf-${pageLabel}-${formatExportTimestamp(new Date())}.pdf`;
}

function triggerBrowserDownload(url: string, fileName: string) {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
}

function formatDimensions(width: number, height: number) {
  return `${width} × ${height}`;
}

function getRotatedDimensions(page: Pick<PdfImagePage, 'width' | 'height' | 'rotation'>) {
  return page.rotation === 90 || page.rotation === 270
    ? { width: page.height, height: page.width }
    : { width: page.width, height: page.height };
}

function getPageSizePoints(
  preset: PageSizePreset,
  orientation: PageOrientation,
  page: Pick<PdfImagePage, 'width' | 'height' | 'rotation'>,
  margin: number,
) {
  const rotated = getRotatedDimensions(page);

  if (preset === 'auto') {
    return {
      width: rotated.width + margin * 2,
      height: rotated.height + margin * 2,
    };
  }

  const base = preset === 'a4' ? { width: 595.28, height: 841.89 } : { width: 612, height: 792 };
  const nextOrientation =
    orientation === 'auto'
      ? rotated.width > rotated.height
        ? 'landscape'
        : 'portrait'
      : orientation;

  return nextOrientation === 'landscape' ? { width: base.height, height: base.width } : base;
}

function getScaledImageSize(width: number, height: number) {
  const longestEdge = Math.max(width, height);

  if (longestEdge <= maxRenderEdge) {
    return { width, height };
  }

  const scale = maxRenderEdge / longestEdge;

  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

async function loadImageFromUrl(url: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('This image could not be decoded in the browser.'));
    image.src = url;
  });
}

async function createPdfImageBytes(page: PdfImagePage, quality: number) {
  const image = await loadImageFromUrl(page.previewUrl);
  const scaled = getScaledImageSize(image.naturalWidth, image.naturalHeight);
  const rotated =
    page.rotation === 90 || page.rotation === 270
      ? { width: scaled.height, height: scaled.width }
      : scaled;

  const canvas = document.createElement('canvas');
  canvas.width = rotated.width;
  canvas.height = rotated.height;

  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Your browser could not prepare this page for PDF export.');
  }

  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.save();

  switch (page.rotation) {
    case 90:
      context.translate(canvas.width, 0);
      context.rotate(Math.PI / 2);
      break;
    case 180:
      context.translate(canvas.width, canvas.height);
      context.rotate(Math.PI);
      break;
    case 270:
      context.translate(0, canvas.height);
      context.rotate(-Math.PI / 2);
      break;
    default:
      break;
  }

  context.drawImage(image, 0, 0, scaled.width, scaled.height);
  context.restore();

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((nextBlob) => resolve(nextBlob), 'image/jpeg', quality);
  });

  if (!blob) {
    throw new Error('The PDF page could not be rendered in your browser.');
  }

  return {
    bytes: new Uint8Array(await blob.arrayBuffer()),
    width: canvas.width,
    height: canvas.height,
  };
}

async function readPdfImagePage(original: File) {
  // TIFF/HEIC/AVIF: decode once to PNG so the preview and export pipeline below work unchanged.
  let file = original;
  if (SOFTWARE_DECODED.test(original.name)) {
    const probe = URL.createObjectURL(original);
    const native = await loadImageFromUrl(probe).then(
      () => true,
      () => false,
    );
    URL.revokeObjectURL(probe);
    if (!native) {
      try {
        file = await decodeToPngFile(original);
      } catch {
        throw new Error(UNDECODABLE_MESSAGE);
      }
    }
  }
  const previewUrl = URL.createObjectURL(file);

  try {
    const image = await loadImageFromUrl(previewUrl);

    return {
      id: crypto.randomUUID(),
      file,
      name: original.name,
      size: file.size,
      width: image.naturalWidth,
      height: image.naturalHeight,
      previewUrl,
      rotation: 0 as const,
    };
  } catch (error) {
    URL.revokeObjectURL(previewUrl);
    throw error;
  }
}

export default function BrowserImageToPdf({
  heading,
  toolSlug = 'image-to-pdf',
}: { heading?: string; toolSlug?: string } = {}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [pages, setPages] = useState<PdfImagePage[]>([]);
  const [activePreset, setActivePreset] = useState<PageSizePreset>('auto');
  const [orientation, setOrientation] = useState<PageOrientation>('auto');
  const [margin, setMargin] = useState<number>(marginOptions[1].value);
  const [quality, setQuality] = useState(0.88);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [resultPageCount, setResultPageCount] = useState<number | null>(null);
  const previewUrlsRef = useRef<string[]>([]);
  const downloadUrlRef = useRef<string | null>(null);

  useEffect(() => {
    previewUrlsRef.current = pages.map((page) => page.previewUrl);
  }, [pages]);

  useEffect(() => {
    return () => {
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));

      if (downloadUrlRef.current) {
        URL.revokeObjectURL(downloadUrlRef.current);
      }
    };
  }, []);

  const totalSourceSize = useMemo(() => pages.reduce((sum, page) => sum + page.size, 0), [pages]);
  const activePresetCopy = pageSizeOptions.find((option) => option.id === activePreset) ?? null;

  function resetExportState() {
    if (downloadUrlRef.current) {
      URL.revokeObjectURL(downloadUrlRef.current);
    }

    downloadUrlRef.current = null;
    setResultSize(null);
    setResultPageCount(null);
  }

  function revokePagePreview(pageId: string) {
    const match = pages.find((page) => page.id === pageId);

    if (match) {
      URL.revokeObjectURL(match.previewUrl);
    }
  }

  async function addFiles(fileList: FileList | File[] | null) {
    if (!fileList?.length) {
      return;
    }

    setErrorMessage(null);
    setCreditMessage(null);
    const imageFiles = Array.from(fileList).filter((file) => file.type.startsWith('image/'));

    if (!imageFiles.length) {
      setErrorMessage('Choose image files like JPG, PNG, WebP, GIF, or BMP.');
      return;
    }

    try {
      const nextPages = await Promise.all(imageFiles.map((file) => readPdfImagePage(file)));
      setPages((current) => [...current, ...nextPages]);
      resetExportState();
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'One or more images could not be loaded.',
      );
    }
  }

  function movePage(pageId: string, direction: -1 | 1) {
    setPages((current) => {
      const index = current.findIndex((page) => page.id === pageId);

      if (index < 0) {
        return current;
      }

      const nextIndex = index + direction;

      if (nextIndex < 0 || nextIndex >= current.length) {
        return current;
      }

      const nextPages = [...current];
      const [item] = nextPages.splice(index, 1);
      nextPages.splice(nextIndex, 0, item);
      return nextPages;
    });
    resetExportState();
  }

  function rotatePage(pageId: string) {
    setPages((current) =>
      current.map((page) =>
        page.id === pageId
          ? {
              ...page,
              rotation: ((page.rotation + 90) % 360) as PdfImagePage['rotation'],
            }
          : page,
      ),
    );
    resetExportState();
  }

  function removePage(pageId: string) {
    revokePagePreview(pageId);
    setPages((current) => current.filter((page) => page.id !== pageId));
    resetExportState();
  }

  function clearPages() {
    pages.forEach((page) => URL.revokeObjectURL(page.previewUrl));
    setPages([]);
    setErrorMessage(null);
    setCreditMessage(null);
    resetExportState();

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }

  async function createPdf() {
    if (!pages.length) {
      setErrorMessage('Add at least one image first.');
      return;
    }

    setIsExporting(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      const idToken = await getToolAuthToken();

      if (idToken) {
        headers.authorization = `Bearer ${idToken}`;
      }

      const usageResponse = await fetch('/api/tools/usage', {
        method: 'POST',
        headers,
        body: JSON.stringify({ toolSlug }),
      });
      const usagePayload = (await usageResponse.json().catch(() => ({}))) as {
        success?: boolean;
        data?: {
          credits?: {
            plan?: 'guest' | 'free' | 'pro';
            remaining?: number | null;
            windowLabel?: string;
          };
        };
        error?: unknown;
      };

      if (!usageResponse.ok || !usagePayload.success) {
        const message =
          typeof usagePayload.error === 'object' &&
          usagePayload.error &&
          'message' in usagePayload.error
            ? String((usagePayload.error as { message?: unknown }).message)
            : typeof usagePayload.error === 'string'
              ? usagePayload.error
              : 'Credits are not available for another PDF export right now.';
        throw new Error(message);
      }

      const pdfDocument = await PDFDocument.create();

      for (const page of pages) {
        const rendered = await createPdfImageBytes(page, quality);
        const embedded = await pdfDocument.embedJpg(rendered.bytes);
        const pdfPageSize = getPageSizePoints(activePreset, orientation, page, margin);
        const pdfPage = pdfDocument.addPage([pdfPageSize.width, pdfPageSize.height]);
        const availableWidth = Math.max(1, pdfPageSize.width - margin * 2);
        const availableHeight = Math.max(1, pdfPageSize.height - margin * 2);
        const scale = Math.min(availableWidth / rendered.width, availableHeight / rendered.height);
        const drawWidth = rendered.width * scale;
        const drawHeight = rendered.height * scale;

        pdfPage.drawImage(embedded, {
          x: (pdfPageSize.width - drawWidth) / 2,
          y: (pdfPageSize.height - drawHeight) / 2,
          width: drawWidth,
          height: drawHeight,
        });
      }

      const pdfBytes = Uint8Array.from(await pdfDocument.save());
      const blob = new Blob([pdfBytes.buffer], { type: 'application/pdf' });
      const nextDownloadUrl = URL.createObjectURL(blob);
      const nextDownloadName = createExportFileName(pages.length);

      resetExportState();
      downloadUrlRef.current = nextDownloadUrl;
      setResultSize(blob.size);
      setResultPageCount(pages.length);
      triggerBrowserDownload(nextDownloadUrl, nextDownloadName);
      setCreditMessage(
        usagePayload.data?.credits?.plan === 'pro'
          ? 'PDF download started. Unlimited plan active.'
          : usagePayload.data?.credits?.remaining !== undefined
            ? `PDF download started. ${usagePayload.data.credits.remaining} credits left in this ${usagePayload.data.credits.windowLabel?.toLowerCase() || 'current'} window.`
            : 'PDF download started.',
      );
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'The PDF could not be created.');
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <article className="tool-live-card tool-pdf-card">
      <div className="tool-live-header">
        <div>
          <h2>{heading ?? 'Turn images into one PDF in the browser'}</h2>
          <p>
            Add multiple images, reorder the pages, choose the paper fit, and export one PDF without
            uploading the files to a server.
          </p>
        </div>
        <span className="tool-live-badge">Browser-first</span>
      </div>

      <div className="tool-live-routes" aria-label="Page size presets">
        {pageSizeOptions.map((option) => (
          <button
            key={option.id}
            className={`tool-live-route ${activePreset === option.id ? 'tool-live-route-active' : ''}`}
            onClick={() => {
              setActivePreset(option.id);
              resetExportState();
            }}
            type="button"
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void addFiles(event.dataTransfer.files);
            }}
            type="button"
          >
            <div>
              <strong>
                {pages.length
                  ? `${pages.length} page${pages.length === 1 ? '' : 's'} ready for PDF export`
                  : 'Choose images or drop them here'}
              </strong>
              <span>
                {pages.length
                  ? `${formatFileSize(totalSourceSize)} total source size • reorder, rotate, and export in one PDF`
                  : 'Works best with JPG, PNG, WebP, GIF, and BMP files'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept={`image/*,.jfif,${SOFTWARE_DECODED_ACCEPT}`}
            className="tool-live-file-input"
            multiple
            onChange={(event) => void addFiles(event.target.files)}
            type="file"
          />

          <div className="tool-live-controls">
            <label className="field">
              <span>Orientation</span>
              <select
                onChange={(event) => {
                  setOrientation(event.target.value as PageOrientation);
                  resetExportState();
                }}
                value={orientation}
              >
                {orientationOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              <span>Page margin</span>
              <select
                onChange={(event) => {
                  setMargin(Number(event.target.value));
                  resetExportState();
                }}
                value={margin}
              >
                {marginOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="tool-editor-sliders tool-pdf-settings">
            <label className="field tool-live-quality">
              <span>Image quality inside PDF</span>
              <input
                max="0.96"
                min="0.7"
                onChange={(event) => {
                  setQuality(Number(event.target.value));
                  resetExportState();
                }}
                step="0.01"
                type="range"
                value={quality}
              />
              <small>{`${Math.round(quality * 100)}% quality`}</small>
            </label>
          </div>

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!pages.length || isExporting}
              onClick={() => void createPdf()}
              type="button"
            >
              {isExporting ? 'Building PDF…' : 'Create PDF'}
            </button>
            {pages.length ? (
              <button className="button button-ghost" onClick={clearPages} type="button">
                Clear pages
              </button>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Client-side PDF builder</h3>
          <div className="tool-mockup-chip-list">
            <span>Images → PDF</span>
            <span>Page order</span>
            <span>Rotate</span>
            <span>Local export</span>
          </div>
          <p>
            {activePresetCopy?.hint ??
              'Pick the page fit first, then export one PDF after the page order feels right.'}
          </p>
          <div className="tool-live-panel-note">
            <strong>Best browser support</strong>
            <span>Upload JPG, PNG, WebP, GIF, or BMP. Export one compact PDF on the device.</span>
          </div>
          {pages.length ? (
            <div className="tool-live-stats">
              <div>
                <strong>Pages</strong>
                <span>{pages.length}</span>
              </div>
              <div>
                <strong>Source total</strong>
                <span>{formatFileSize(totalSourceSize)}</span>
              </div>
              <div>
                <strong>Paper fit</strong>
                <span>{activePresetCopy?.label ?? 'Auto fit'}</span>
              </div>
              <div>
                <strong>Orientation</strong>
                <span>{orientation === 'auto' ? 'Auto' : orientation}</span>
              </div>
              <div>
                <strong>Margin</strong>
                <span>
                  {marginOptions.find((option) => option.value === margin)?.label ?? 'Balanced'}
                </span>
              </div>
              <div>
                <strong>Quality</strong>
                <span>{`${Math.round(quality * 100)}%`}</span>
              </div>
              {resultSize ? (
                <div>
                  <strong>PDF size</strong>
                  <span>{formatFileSize(resultSize)}</span>
                </div>
              ) : null}
              {resultPageCount ? (
                <div>
                  <strong>Last export</strong>
                  <span>{`${resultPageCount} page${resultPageCount === 1 ? '' : 's'}`}</span>
                </div>
              ) : null}
              <div>
                <strong>Privacy</strong>
                <span>Processed in your browser</span>
              </div>
            </div>
          ) : null}
        </aside>
      </div>

      {pages.length ? (
        <div className="tool-live-preview tool-pdf-preview">
          <h3>Pages</h3>
          <div className="tool-pdf-page-list">
            {pages.map((page, index) => {
              const rotated = getRotatedDimensions(page);

              return (
                <article className="tool-pdf-page-card" key={page.id}>
                  <div className="tool-pdf-page-thumb">
                    <img alt={`${page.name} preview`} src={page.previewUrl} />
                  </div>
                  <div className="tool-pdf-page-copy">
                    <strong>{`${index + 1}. ${page.name}`}</strong>
                    <span>{`${formatDimensions(rotated.width, rotated.height)} • ${formatFileSize(page.size)}`}</span>
                    <span>{`Rotation ${page.rotation}°`}</span>
                  </div>
                  <div className="tool-pdf-page-actions">
                    <button
                      className="tool-pdf-page-action"
                      disabled={index === 0}
                      onClick={() => movePage(page.id, -1)}
                      type="button"
                    >
                      Up
                    </button>
                    <button
                      className="tool-pdf-page-action"
                      disabled={index === pages.length - 1}
                      onClick={() => movePage(page.id, 1)}
                      type="button"
                    >
                      Down
                    </button>
                    <button
                      className="tool-pdf-page-action"
                      onClick={() => rotatePage(page.id)}
                      type="button"
                    >
                      Rotate
                    </button>
                    <button
                      className="tool-pdf-page-action tool-pdf-page-action-danger"
                      onClick={() => removePage(page.id)}
                      type="button"
                    >
                      Remove
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      ) : null}
    </article>
  );
}
